import { AbstractControl, FormGroupDirective, NgForm, UntypedFormControl, UntypedFormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';

import { ErrorStateMatcher } from '@angular/material/core';
import { v1 as uuid } from 'uuid';
import {
    ATM_RANGE,
    COOKIE_NAMES,
    CURRENCY_LABEL, DEFAULT_PRECISION,
    EPOCH_EXPIRES,
    GMT_IST_DIFFERENCE,
    HFT_TIME_FORMAT, MOMENT_COMPLETE_TIME,
    MOMENT_DATE_FORMAT,
    MOMENT_OPTION_FORMAT,
    PORTFOLIO_NAME_TIME_FORMAT,
    REG_EX,
    REJECTED_ORDERS,
    SELECTED_VENDOR,
    STRATEGY_CREATION_METHOD,
    SymbolMapSpaceSeparated,
    TIME_FORMAT,
    exchangesKeys
} from './constants';

import * as memoizee from 'memoizee';

import { HttpHeaders } from '@angular/common/http';
import { AlertOptions, HELP_SECTION_STRINGS, SearchSymbolResults, SingleIndexQuotes, SocketDepth, StrikePriceFilter, subscribeQuoteParams } from '@app/_shared';
import { ChartDataResponse } from '@app/_shared/modules/chart/chart.interface';
import { PriceType } from '@app/_shared/modules/order-form/order-form.enums';
import { COMPARATORS_STRING, EXCHANGE_STRINGS, EXIT_EXECUTION_DAYS_VALUES, NAME_STRING, PRODUCT_OPTIONS } from '@app/_shared/modules/portfolio-form/portfolio-form.enums';
import { DependentLeg, PortfolioDisplayResponse, executionDaysParams } from '@app/_shared/modules/portfolio-form/portfolio.interface';
import { PortfolioModel } from '@app/_shared/modules/portfolio-form/portfolio.model';
import { ALLOWED_STRATEGY_DAYS } from '@app/_shared/modules/strategy-form/strategy.constants';
import { BacktestPermissions } from '@app/_shared/providers/configuration/configuration.interfaces';
import { MarginResponse, TransactionDetailsResponse } from '@app/_shared/providers/funds/funds.interface';
import { OrderStatus } from '@app/_shared/shared.structs';
import { HelpInfoInterface, HelpOptionsInterface } from '@app/app.interface';
import { Exchange, Instrument, MarketKeywords, OptionTypeAbbreviatedEnum, OptionTypeEnum, Series, SortingTypes } from '@app/app.structs';
import { ExchangePermissionMappingRev } from '@app/modules/admin/components/plan-n-pricing/components/plan-pricing-detail/components/exchange-permissions-form/exchange-permissions-mapping';
import { PlanDetailI } from '@app/modules/admin/providers/plan-n-pricing/plan-n-pricing.interfaces';
import { NetUserProfit } from '@app/modules/trading/modules/dashboard/components/margin-summary/margin-summary.interfaces';
import { MarketPlaceCardsType } from '@app/modules/trading/modules/dashboard/components/marketplace-cards/marketplace-cards.consts';
import { BriefPortfolioModel } from '@app/modules/trading/modules/market-place/providers/market-place/market-place.models';
import { ButtonStateType, MyButtonType, MyStrategyType } from '@app/modules/trading/modules/my-strategies/my-strategies.consts';
import { GreekResponseStruct } from '@app/modules/trading/modules/pay-off-curve/modules/rapid/providers/legs/legs.constants';
import { TransactionStatus } from '@app/modules/trading/modules/profile/components/funds/funds.enums';
import { SymbolsSelected, frequencyMappingMultiple } from '@app/modules/trading/modules/profile/components/plans/plans.enums';
import imageCompression, { Options } from 'browser-image-compression';
import * as CryptoJS from 'crypto-js';
import { find, pickBy } from 'lodash';
import * as moment from 'moment';
import { MASTERTRUST_SECRET_KEY, SWASTIKA_SECRET_KEY } from '../../../src/environments/environment';
import { OhlcDataResponse } from '../../utProto/chartingData_pb';
import { ExchangeId, OptionMode, OrderMode, ProductType } from '../../utProto/enums_pb';
import { ExecutionDaysType, ExitExecutionDays, LegExitTypes, MtmCheckType, UnderlyingValueTypes } from '../../utProto/webApiStrategyEnums_pb';
import { AlgoPermissions, PortfolioShareStatus } from '../../utProto/webEnums_pb';
import { XlsxDownloadHelper } from './download-helper';
import { CatalogPlan, CurrentPlan, UserPlan } from '@app/modules/trading/modules/mb-plans/providers/mb-plans.interface';
import { CANCEL_BUTTON_TEXT, CONFIRM_BUTTON_TEXT } from './confirmation-dialog.enums';


export class FormControlErrorMatcher implements ErrorStateMatcher {
    isErrorState(control: UntypedFormControl | null, form: FormGroupDirective | NgForm | null): boolean {
        // const isSubmitted = form && form.submitted;
        const stateMatcherValue = !!(control && control.invalid);
        return stateMatcherValue;
    }
}


// tslint:disable-next-line:no-namespace
export namespace Utils {

    export function confirmationDialogParams(msg: string, handlerFunc, updateCopyPortfolioVersion?: any,
        cancelBtnText: CANCEL_BUTTON_TEXT = CANCEL_BUTTON_TEXT.default, confirmBtnText: CONFIRM_BUTTON_TEXT = CONFIRM_BUTTON_TEXT.default) {
        const cancelBtnColor = (cancelBtnText === CANCEL_BUTTON_TEXT.default) ? 'bg-primary-dark' : 'undeploy-button';
        const options: AlertOptions = {
            message: msg,
            buttons: [
                {
                    text: cancelBtnText,
                    accent: true,
                    class: cancelBtnColor,
                    handler: updateCopyPortfolioVersion
                },
                {
                    text: confirmBtnText,
                    accent: true,
                    class: 'new-theme-gradient-bg',
                    handler: handlerFunc
                }
            ]
        };

        return options;
    }

    export function makeConditionExpression(isTargetProfit: boolean) {
        return isTargetProfit ? `MTM >= TP` : `MTM <= SL`;
    }

    export function markTouched(form: UntypedFormGroup) {
        Object.keys(form.controls)
            .forEach(key => {
                const control = form.controls[key];
                control.markAsTouched();
            });
        return;
    }

    // Convert Date to other format
    export function convertDateFormat(date: any, inputFormat: string, outputFormat: string) {
        return (moment(date, inputFormat).format(outputFormat));
    }

    // Convert Date to time
    export function dateToTimeConvert(date, format = HFT_TIME_FORMAT) {
        if (date) {
            return moment(date).format(format);
        } else {
            return 0;
        }
    }
    // Convert time to date
    export function timeToDateConvert(time) {
        //Insert 0 at start if time is not in required format
        let timeString = time?.toString().padStart(6, 0);
        return moment(timeString, HFT_TIME_FORMAT).toDate();

    }

    export function getCurrentDateOnly() {
        return moment().format(MOMENT_DATE_FORMAT)
    }

    export function getDateOnlyFromEpoch(epoch: number) {
        return moment.unix(epoch).format(MOMENT_DATE_FORMAT)
    }

    /**
     * Returns the current Unix timestamp in seconds.
     * @returns {number} The current Unix timestamp (seconds since epoch).
     */
    export function getCurrentUnixTimestamp(): number {
        return Math.floor(Date.now() / 1000);
    }

    export function convertTimeInMilliseconds(seconds: number) {
        return seconds * 1000;
    }

    export function convertDateTime(modified: number) {
        return modified > 10**11 ? modified : modified * 1000;
    }

    // Get Time format unique name
    export function getUniqueName(type, portfolioType?: STRATEGY_CREATION_METHOD) {
        const currentDate = moment();
        let formattedDate = currentDate.format(PORTFOLIO_NAME_TIME_FORMAT);
        if (type == NAME_STRING.PORTFOLIO) {
            if (portfolioType && portfolioType === STRATEGY_CREATION_METHOD.TVPORTFOLIO) {
                formattedDate = 'Temp_'.concat(formattedDate);
            }
            else {
                formattedDate = 'PF_'.concat(formattedDate);
            }
        } else if (type == NAME_STRING.STRATEGY) {
            formattedDate = 'SG_'.concat(formattedDate);
        }
        return formattedDate;
    }

    //Get string of remaining time from present date in d:hr:m format
    export function getRemainingTimeFromPresent(endDate: any) {
        const endTimestamp = new Date(endDate).getTime();
        const now = new Date().getTime();
        const distance = endTimestamp - now;
        if (distance < 0) {
            return '';
        }
        const daysRemaining = Math.floor(distance / (1000 * 60 * 60 * 24));
        const hoursRemaining = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutesRemaining = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
        return `${daysRemaining}d ${hoursRemaining}hr ${minutesRemaining}m left`;
    }

    /**
     * This is utility function for adding delay in code (sleeping for some time)
     *
     * @param time Time for sleeping
     * @returns Promise after awaking
     */
    export function sleep(time: number = 0) {
        return new Promise(resolve => setTimeout(resolve, time));
    }

    export function validateDuplicateStrategyName(strategies: string[]) {
        return (control: AbstractControl) => {
            const trimmedStrategies = strategies.map(s => s.trim());
            const isDuplicate = trimmedStrategies.includes(control.value.trim());
            return isDuplicate ? { duplicate: true } : null;
        };
    }

    export function preventEvent(event) {
        event.preventDefault();
        event.stopPropagation();
    }

    
    /**
     * Masks an email address for privacy by replacing characters with asterisks.
     * 
     * Privacy pattern:
     * - Shows first and last character of local part (before @)
     * - Masks middle characters with asterisks (max 8 asterisks)
     * - Shows full domain if <= 30 characters
     * - Masks domain (first + asterisks + last) if > 30 characters
     * 
     * Edge cases handled:
     * - Empty/null email returns empty string
     * - Invalid email format (no @) returns original
     * - Single character local part shows just that character
     * - Two character local part shows both without masking
     * 
     * @param email - Email address to mask
     * @returns Masked email string in format: "a****z@domain.com" or "a****z@e****m"
     * 
     * @example
     * maskEmail('john.doe@example.com')     // Returns: 'j******e@example.com'
     * maskEmail('a@example.com')            // Returns: 'a@example.com'
     * maskEmail('ab@example.com')           // Returns: 'ab@example.com'
     * maskEmail('test@verylongdomainname.com') // Returns: 't**t@verylongdomainname.com'
     */
    export function maskEmail(email: string): string {
        const MAX_MASKED_LENGTH = 8;
        const DOMAIN_MASK_THRESHOLD = 30;
        
        // Handle empty/null input
        if (!email) {
            return '';
        }
        
        // Validate email format
        const [localPart, domain] = email.split('@');
        if (!domain) {
            return email; // Invalid format, return as-is
        }
        
        // Handle edge cases for local part length
        const localLength = localPart.length;
        if (localLength === 0) {
            return email; // Malformed email
        }
        
        // Mask local part: first char + asterisks + last char
        const firstChar = localPart[0];
        const lastChar = localPart[localLength - 1];
        const middleLength = Math.min(localLength - 2, MAX_MASKED_LENGTH);
        const maskedLocal = localPart.length > 2 ? `${firstChar}${'*'.repeat(middleLength)}${lastChar}` : localPart;
        
        // Mask domain if it exceeds threshold
        if (domain.length > DOMAIN_MASK_THRESHOLD) {
            const domainFirstChar = domain[0];
            const domainLastChar = domain[domain.length - 1];
            const maskedDomain = `${domainFirstChar}${'*'.repeat(MAX_MASKED_LENGTH)}${domainLastChar}`;
            return `${maskedLocal}@${maskedDomain}`;
        }
        
        return `${maskedLocal}@${domain}`;
    }

    /**
     * Masks a phone: shows last 4 digits → "******7890"
     */
    export function maskPhone(phone: string): string {
        if (!phone) return '';
        const digits = phone.replace(/\D/g, '');
        if (digits.length <= 4) return digits;
        return '*'.repeat(digits.length - 4) + digits.slice(-4);
    }

    export function localeConverter(value: any, precision: number) {
        const valueI = parseFloat(value);
        return !isNaN(valueI) ? valueI.toLocaleString(SELECTED_VENDOR.locale, { maximumFractionDigits: precision }) : value;
    }


    /**
     * Function replaces/add value to a query param in a url.
     * @param originalUrl original url string to be replaced.
     * * @param replaceParam parameter to be replaced.
     * * @param newValue value to be added.
     * @returns  Updated string with replaced param.
     */
    export function addParamToStringUrl(originalUrl: string, replaceParam: string, newValue: any): string {
        const urlObject = new URL(originalUrl);

        // Get the search parameters
        const searchParams = new URLSearchParams(urlObject.search);

        searchParams.set(replaceParam, newValue);

        // Update the search portion of the URL
        urlObject.search = searchParams.toString();

        // Get the updated URL as a string
        const updatedUrl = urlObject.toString();

        return updatedUrl;
    }

    /**
     * Function to search for a specific query parameter in a URL string.
     * @param url The URL string to search in.
     * @param paramName The name of the query parameter to search for.
     * @returns The value of the query parameter if found, or null if not found.
     */
    export function searchQueryParam(url: string, paramName: string): string | null {
        const urlObject = new URL(url);

        // Get the search parameters
        const searchParams = new URLSearchParams(urlObject.search);

        // Check if the query parameter exists
        if (searchParams.has(paramName)) {
            return searchParams.get(paramName);
        }

        return null;
    }

    /**
     * Function that convert HHmmss to hh:mm z format
     * @param time HHmmss time
     * @returns  hh:mm z format time
     */
    export function convertTime(time: any) {
        time = time.toString();
        const len = time.toString().length;
        if (len < 6) {
            time = '0' + time;
        }
        let hour = time.slice(0, 2);
        const minute = time.slice(2, 4)
        const amOrPm = +hour > 12 ? 'PM' : 'AM';
        if (amOrPm === 'PM') {
            hour = hour - 12;
        }
        return hour + ':' + minute + amOrPm;
    }

    export function getTransactionTime(row: TransactionDetailsResponse) {
        if (row.status === TransactionStatus.Pending) {
            return row.start_time;
        } else {
            return row.last_updated_time;
        }
    }

    export function greeksPrecision(column) {
        return GreekResponseStruct.find(el => el.title === column)?.precision || null;
    }

    export function handleFileChange(event: Event, acceptFileType: string[], fileSize = null): Promise<string> {
        const file: File = (event.target as HTMLInputElement).files[0];
        // const fileType: any = file && file.name && file.name.split('.')[1].toLowerCase();
        let errObject = {};
        return new Promise((resolve, reject) => {
            try {
                let fileType: string;
                let size: number;
                if (file && file.name) {
                    const fileNameArr = file.name.split('.');
                    const extention = fileNameArr[fileNameArr.length - 1];
                    size = file.size;
                    fileType = extention.toLowerCase();
                }

                if (fileSize) {
                    const sizeInBytes = fileSize;
                    if (sizeInBytes > size) {
                        errObject = { error: 'File is too large' };
                        reject(errObject);
                    }
                }
                acceptFileType = acceptFileType.map(res => res ? res.toLowerCase() : res);

                if (fileType && acceptFileType.indexOf(fileType) !== -1) {
                    const reader = new FileReader();
                    reader.onload = ((_) => {
                        return (e: ProgressEvent) => {
                            // const base64Result = ((e.target as FileReader).result as string).replace(new RegExp('data.*?,'), '');
                            // const data = atob(base64Result);
                            const data = ((e.target as FileReader).result as string);
                            resolve(data);
                        };
                    })(file);
                    reader.readAsDataURL(file);
                } else {
                    errObject = { error: `Only ${acceptFileType.join(', ')} file types are allowed ` };
                    reject(errObject);
                }
            } catch (_) {
                errObject = { error: 'Something went wrong' };
                reject(errObject);
            }
        });
    }

    export function getColumnData(dataSource: any, columnName: string) {
        const retArray = [];
        dataSource.forEach(element => {
            retArray.push(element[columnName]);
        });
        return retArray;
    }

    export function changeTime(day: number, month: number, year: number): moment.Moment {
        //Backtest is not allowed for the same day so we need to calculate time period from yesterday
        return moment().subtract(1, 'days').subtract({
            days: day,
            months: month,
            years: year
        })
    }

    export function extractFromSymbolId(symbolId: number): number[] {
        let retValue = [];
        const strSymbolId = symbolId.toString()
        return [+strSymbolId.slice(0, 1), +strSymbolId.slice(1, 4), +strSymbolId.slice(4, 5), +strSymbolId.slice(5)]
    }

    export function formatIndicatorValueString(indicator: string, ...params: any[]) {
        // Filter out null values
        params = params.filter(p => (p !== null))
        return `${indicator.trim()}` + (params.length ? `(${params.join()})` : '');
    }

    export function parseIndicatorValueString(valueString: string) {
        if (!valueString.endsWith(')')) {
            // If Value string does not ends with ')' then it is zero parameter indicator
            // Like :- Close, Open, LTP etc.
            // If it is that, then return `valueString` as it will be indicator name only ('LTP')
            return { name: valueString, params: [] };
        }
        const indicatorArr = valueString.split('(');
        const indicatorName = indicatorArr[0];
        const indicatorParams = indicatorArr[1].split(')')[0].split(',');
        return { name: indicatorName, params: indicatorParams };
    }

    export function getSymbolId(marketId: number, securityId: number) {


        /* new symbol creation method
           3 digits will be reserved for exchange id  and 10 digits will be reserved for security id
           1       2 3 4      5             6 7 8 9 10 11 12 13 14 15
           source   exchange contract type         security id
           for example NSECM symbol with security id 13771 and souce default will have symbol id as
           1       003       0                 0000013771
           100300000013771
        */

        const source = '1';
        const exchange = marketId?.toString().padStart(3, '0');
        const contractType = '0';
        const paddedSecurityId = securityId?.toString().padStart(10, '0');

        return +`${source}${exchange}${contractType}${paddedSecurityId}`;

    }

    export function getRandomString(length?: number): string {
        let random: string = uuid();
        random = btoa(random);
        return length != null ? random.substring(0, length) : random;
    }

    export function getUnionOfRanges(range1: number[], range2: number[]) {
        return [Math.max(range1[0], range2[0]), Math.min(range1[1], range2[1])];
    }

    export function getRandomTokenId() {
        return +((new Date()).getTime() + Math.round(Math.random() * 1e6)).toString().substring(5)
    }

    export function delay(time: number): Promise<void> {
        return new Promise((resolve, _) => {
            setTimeout(() => {
                resolve();
            }, time);
        });
    }

    export function highlightInvalidFormFields(control: object) {
        Object.keys(control).forEach((element: any) => {
            control[element].markAsDirty();
            control[element].markAsTouched();
        });
    }

    export function isFieldInvalid(control: UntypedFormControl | any) {
        return Boolean((control && control.invalid && (control.dirty || control.touched)));
    }

    // Function to get underlying type, SL or TP
    export function targetStopLossCalculator(orderMode: string, instrumentType: string, isMin: boolean) {
        if (orderMode === OrderMode.ORDERMODE_BUY.toString() &&
            instrumentType === OptionMode.OPTIONMODE_CALL.toString() ||
            orderMode === OrderMode.ORDERMODE_SELL.toString() &&
            instrumentType === OptionMode.OPTIONMODE_PUT.toString()) {
            return isMin ? UnderlyingValueTypes.UNDERLYINGVALUETYPES_STOP_LOSS :
                UnderlyingValueTypes.UNDERLYINGVALUETYPES_TARGET_PROFIT;

        } else if (orderMode === OrderMode.ORDERMODE_SELL.toString() &&
            instrumentType === OptionMode.OPTIONMODE_CALL.toString() ||
            orderMode === OrderMode.ORDERMODE_BUY.toString() &&
            instrumentType === OptionMode.OPTIONMODE_PUT.toString()) {
            return isMin ? UnderlyingValueTypes.UNDERLYINGVALUETYPES_TARGET_PROFIT :
                UnderlyingValueTypes.UNDERLYINGVALUETYPES_STOP_LOSS
        }

    }

    export function getRequestHeadersSwastika() {
        const currDate = new Date();
        const requestKey = `${Utils.getRandomString()}:${Math.floor(currDate.getTime() / 1000)}`;

        const hmac = CryptoJS.HmacSHA256(CryptoJS.enc.Utf8.parse(requestKey), CryptoJS.enc.Utf8.parse((SWASTIKA_SECRET_KEY)));

        const hexHMAC = hmac.toString(CryptoJS.enc.Hex);

        const headers = new HttpHeaders({
            'request-key': requestKey,
            'request-secret': hexHMAC
        });

        return headers;
    }

    export function getRequestHeadersMastertrust() {
        const currDate = new Date();
        const requestKey = `${Utils.getRandomString()}:${Math.floor(currDate.getTime() / 1000)}`;

        const hmac = CryptoJS.HmacSHA256(CryptoJS.enc.Utf8.parse(requestKey), CryptoJS.enc.Utf8.parse((MASTERTRUST_SECRET_KEY)));

        const hexHMAC = hmac.toString(CryptoJS.enc.Hex);

        const headers = new HttpHeaders({
            'request-key': requestKey,
            'request-secret': hexHMAC
        });

        return headers;
    }

    export namespace AesGcmEncryption {

        const format = 'raw';
        const algorithm = 'AES-GCM';

        /**
         * Encodes the given ArrayBuffer as base64 URL-safe string.
         * 
         * @param buffer - The ArrayBuffer to be encoded.
         * @returns The base64 URL-safe encoded string.
         */
        function base64UrlEncode(buffer: ArrayBuffer): string {
            let base64 = btoa(String.fromCharCode.apply(null, new Uint8Array(buffer)));
            base64 = base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
            while (base64.length % 4) {
                base64 += '=';
            }
            return base64;
        }

        /**
         * Decodes a base64 URL string into a Uint8Array.
         * @param base64 The base64 URL string to decode.
         * @returns The decoded Uint8Array.
         */
        function base64UrlDecode(base64: string): Uint8Array {
            base64 = base64.replace(/-/g, '+').replace(/_/g, '/');
            return Uint8Array.from(atob(base64), c => c.charCodeAt(0));
        }

        /**
         * Encrypts the request body using AES-GCM encryption algorithm.
         * @param stringifiedBody - The stringified request body to be encrypted.
         * @param key - The encryption key.
         * @param secret - The encryption secret.
         * @returns The encrypted request body as a base64 URL-encoded string.
         */
        export async function encryptRequestBody(stringifiedBody: string, key: string, secret: string): Promise<string> {
            const enc = new TextEncoder();
            const keyBytes = enc.encode(key);
            const secretBytes = enc.encode(secret);
            const iv = crypto.getRandomValues(new Uint8Array(12));

            const cryptoKey = await crypto.subtle.importKey(
                format,
                keyBytes,
                { name: algorithm },
                false,
                ['encrypt']
            );

            const ciphertext = await crypto.subtle.encrypt(
                {
                    name: algorithm,
                    iv: iv,
                    additionalData: secretBytes
                },
                cryptoKey,
                enc.encode(stringifiedBody)
            );

            const result = new Uint8Array(iv.length + ciphertext.byteLength);
            result.set(iv);
            result.set(new Uint8Array(ciphertext), iv.length);

            return base64UrlEncode(result.buffer);
        }

        /**
         * Decrypts the encrypted request body using AES-GCM algorithm.
         * @param encryptedBody - The encrypted body to be decrypted.
         * @param key - The encryption key.
         * @param secret - The secret used for additional data in decryption.
         * @returns A promise that resolves to the decrypted body.
         */
        export async function decryptRequestBody(encryptedBody: string, key: string, secret: string): Promise<string> {
            const enc = new TextEncoder();
            const keyBytes = enc.encode(key);
            const secretBytes = enc.encode(secret);

            const data = base64UrlDecode(encryptedBody);
            const iv = data.slice(0, 12);
            const ciphertext = data.slice(12);

            const cryptoKey = await crypto.subtle.importKey(
                format,
                keyBytes,
                { name: algorithm },
                false,
                ['decrypt']
            );

            const decrypted = await crypto.subtle.decrypt(
                {
                    name: algorithm,
                    iv: iv,
                    additionalData: secretBytes
                },
                cryptoKey,
                ciphertext
            );

            return new TextDecoder().decode(decrypted);
        }
    }
    
    export function maxDate(requiredAge: number) {
        const today = new Date();
        const age = today.getFullYear() - requiredAge;
        const m = today.getMonth();
        const d = today.getDate();
        return new Date(`${age}/${m + 1}/${d}`);
    }

    /**
     * This utility function can be used to remove duplicates from an array of objects based an a certain parameter.
     * @param arr the array itself
     * @param comp commom parameter
     */
    export function getUnique(arr, comp) {
        const unique = arr
            .map(e => e[comp])

            // store the keys of the unique objects
            .map((e, i, final) => final.indexOf(e) === i && i)

            // eliminate the dead keys & store unique objects
            .filter(e => arr[e])
            .map(e => arr[e]);

        return unique;
    }

    export function getCopyName(strategyName, count: number) {
        if (count > 0) {
            return `Copy of ${strategyName}_${count}`
        }
        return `Copy of ${strategyName}`;
    }

    export function getRandomNumber(min = 0, max = 10e4) {
        return +((Math.random() * (min - (-max)) + (min)));
    }

    export function applyPrecision(number, precision = DEFAULT_PRECISION) {
        if (number === undefined || number === null) {
            return undefined;
        }
        const memoizedPower = memoizee(power);
        return parseFloat(number) / memoizedPower(precision);
    }

    function power(precision: number): number {
        return Math.pow(10, precision);
    }

    // Converts date string to corresponding epoch
    export function convertStringToUnix(data: string, format: string) {
        return moment(data, format, true).unix()
    }

    export function limitDecimalToDefault(value) {
        return parseFloat(value.toFixed(DEFAULT_PRECISION));
    }

    export function generateStr(indicator: any, idx: number, outerComparator: string[]) {
        try {
            let retStr = '';
            if (idx !== 0) {
                retStr += outerComparator[idx - 1].toUpperCase() + ' ';
            }
            retStr += `${formatIndicator(indicator.indicatorA, indicator.indicatorAParams)} `
                + `${indicator.comparator} ${formatIndicator(indicator.indicatorB, indicator.indicatorBParams)} `;
            return retStr;
        } catch (_) {
            console.log('Error in generate string', _, 'Indicator', indicator);
            return '';
        }
    }

    function getDateString(data: string) {
        data = data.split('-').reverse().join('');
        return data;
    }

    /**
    * Function to check if a string is json or not.
    */
    export function isJson(str: string) {
        try {
            JSON.parse(str);
        } catch (e) {
            return false
        }
        return true;
    }

    export function generateFakeDepth(rows: number = 5, depth = []) {
        function createObj(depthArr: Array<string>) {
            return {
                ask: depthArr[0],
                bid: depthArr[1],
                askMktVol: depthArr[2],
                bidMktVol: depthArr[3],
                numberOfBids: depthArr[4],
                numberOfAsks: depthArr[5],
            };
        }
        for (let i = 0; i < rows; i++) {
            depth.push(createObj(['--', '--', '--', '--', '--', '--']));
        }
        return depth;
    }

    export function convertDepth(depth: any, scrip_precision: number, symbol_id: string) {
        const depthData = new DataView(depth);
        let maxBuyQty = 1;
        let maxSellQty = 1;
        
        const depthObj: SocketDepth = {
            buy: [],
            sell: [],
            symbol_id: symbol_id,
            maxBidQty: 0,
            maxAskQty: 0
        };
    
        for (let i = 0; i < depthData.byteLength; i += 32) {
            const bidQty = depthData.getInt32(i + 4, true);  // true for little endian
            const askQty = depthData.getInt32(i + 20, true);
            maxBuyQty = Math.max(bidQty, maxBuyQty);
            maxSellQty = Math.max(askQty, maxSellQty);
    
            const buyPrice = Utils.applyPrecision(depthData.getInt32(i, true), scrip_precision);
            const sellPrice = Utils.applyPrecision(depthData.getInt32(i + 16, true), scrip_precision);
    
            depthObj.buy.push({
                qty: bidQty,
                no_of_order: depthData.getInt32(i + 12, true),
                price: buyPrice
            });
    
            depthObj.sell.push({
                qty: askQty,
                no_of_order: depthData.getInt32(i + 28, true),
                price: sellPrice
            });
        }
    
        depthObj.maxBidQty = maxBuyQty;
        depthObj.maxAskQty = maxSellQty;
    
        return depthObj;
      }

    export function getInstrumentName(symbol) {

        let symbolString = symbol.symbol.toUpperCase()
        if (SymbolMapSpaceSeparated[symbol.symbol]) {
            symbolString = SymbolMapSpaceSeparated[symbol.symbol];
        }

        let instrument_name = `DEFAULT ${symbol.market_id} ${symbolString}`;
        const instrument_type = symbol.instrument_type;
        if (Instrument[instrument_type] === Instrument['Equity']) {
            if (((symbol.market_id !== MarketKeywords.BSECM) && (symbol.series !== Series.A)) ||
                ((symbol.market_id !== MarketKeywords.NSECM) && (symbol.series !== Series.EQ))) {
                instrument_name += ' ' + symbol.series;
            }
        }
        if (instrument_type === Instrument['Future']) {
            instrument_name += ' ' + getDateString(symbol.expiry);
            // To handle the case of spread, straddle and paired
            if (symbol.expiry_date2) {
                instrument_name += ' ' + getDateString(symbol.expiry_date2);
            }
        }
        if (instrument_type === Instrument['Option']) {
            const optionType = (symbol.option_type.toLowerCase() === OptionTypeEnum.CALL) ? OptionTypeAbbreviatedEnum.CALL : OptionTypeAbbreviatedEnum.PUT;
            const strikePrice = symbol.strike_price * Math.pow(10, symbol.scrip_precision);
            instrument_name += ' ' + getDateString(symbol.expiry) + ' ' + strikePrice + ' ' + optionType;
        }
        return instrument_name;
    }

    export function formatIndicator(name: string, params: { [key: string]: string | number }) {
        try {
            const indicator = name.split(' ').map(x => x.charAt(0).toUpperCase().replace(/[^a-zA-Z ]/g, '')).join('');
            const paramsValue = Object.values(params);
            if (!paramsValue.length) {
                return indicator;
            }
            return `${indicator}(${paramsValue})`;
        } catch (_) {
            console.log('Error in format indicator', _);
            return '';
        }
    }

    /**
     * Function to convert input to currency format
     *
     */
    export function currencyString(value) {
        return `${CURRENCY_LABEL}${value}`;
    }

    /**
     * Function For converting rgb to hex
     */

    export function rgbToHex(hex: string) {
        const r = parseInt(hex.substring(1, 3), 16);
        const g = parseInt(hex.substring(3, 5), 16);
        const b = parseInt(hex.substring(5, 7), 16);
        const rgb = r + ',' + g + ',' + b;
        return { r, g, b, rgb };
    }

    export function istToGmt(time: number): number {
        return time - (GMT_IST_DIFFERENCE);
    }

    export function gmtToIst(time: number): string {
        return Math.ceil((time + GMT_IST_DIFFERENCE) / 1000).toString();
    }

    export function getFromDate(resolution, isTradingView?: boolean) {
        const yesterday = new Date();

        if (isTradingView) {
            if (resolution === '1m') {
                yesterday.setDate(yesterday.getDate() - 1);
            } else if (resolution === '1h') {
                yesterday.setDate(yesterday.getDate() - 5);
            } else if (resolution === '1d') {
                yesterday.setMonth(yesterday.getMonth() - 1);
            } else if (resolution === '1w') {
                yesterday.setFullYear(yesterday.getFullYear() - 1);
            } else if (resolution === '1M') {
                yesterday.setFullYear(yesterday.getFullYear() - 5);
            } else {
                yesterday.setFullYear(yesterday.getFullYear() - 1);
            }
        } else {
            if (
                resolution[resolution.length - 1] === 'h' ||
                resolution[resolution.length - 1] === 'm'
            ) {
                yesterday.setDate(yesterday.getDate() - 1);
            } else if (resolution[resolution.length - 1] === 'd') {
                yesterday.setDate(yesterday.getDate() - 7);
            } else if (resolution[resolution.length - 1] === 'w') {
                yesterday.setMonth(yesterday.getMonth() - 1);
            } else {
                yesterday.setFullYear(yesterday.getFullYear() - 1);
            }
        }

        return gmtToIst(yesterday.getTime());
    }

    export function getToDate() {
        const today = new Date();
        return gmtToIst(today.getTime());
    }

    export function getDaysDiff(startDate, endDate) {
        let endDateObj = moment(endDate);
        let startDateObj = moment(startDate);

        return `${endDateObj.diff(startDateObj, 'days') + 1} days`;
    }

    export function secondsToDuration(seconds: number) {
        const duration = moment.duration(seconds, 'seconds');
        let returnStr = '';

        if (duration.years()) {
            returnStr += ` ${duration.years()}y`;
        }

        if (duration.months()) {
            returnStr += ` ${duration.months()}m`;
        }

        if (duration.days()) {
            returnStr += ` ${duration.days()}d`;
        }

        if (duration.hours()) {
            returnStr += ` ${duration.hours()}h`;
        }

        if (duration.minutes()) {
            returnStr += ` ${duration.minutes()}m`;
        }

        if (duration.seconds()) {
            returnStr += ` ${duration.seconds()}s`;
        }

        return returnStr;
    }

    export class TwoWAyDict {
        public obj = {}
        constructor(object: Object) {
            Object.entries(object).forEach(entry => {
                this.obj[entry[0]] = entry[1];
                this.obj[entry[1]] = entry[0];
            })
        }
    
        getDict() {
            return this.obj;
        }
    }

    export function getFormValidationErrors(control: UntypedFormGroup) {

        Object.keys(control.controls).some((key) => {

            const controlErrors: ValidationErrors = control.get(key).errors;
            if (controlErrors != null) {
                Object.keys(controlErrors).some((keyError) => {
                    console.log('Key control: ' + key + ', keyError: ' + keyError + ', err value: ', controlErrors[keyError]);
                    return true;
                });
                return true;
            }
        });
    }
    // Convert an integer to an array of "bytes" in network/big-endian order.
    export function htonl(n: number) {
        // Mask off 8 bytes at a time then shift them into place
        const bits = [
            (n & 0xFF000000) >>> 24,
            (n & 0x00FF0000) >>> 16,
            (n & 0x0000FF00) >>> 8,
            (n & 0x000000FF) >>> 0,
        ]
        return bits;
    }

    /**
     * Convert a 32-bit quantity (long integer) from network byte order to host byte order (Big-Endian to Little-Endian).
     *
     * @param {Array|Buffer} b Array of octets or a Array Buffer to read value from
     * @param {number} i Zero-based index at which to read from b
     * @returns {number}
     */
    export function ntohl(b: ArrayBuffer, i: number = 0) {
        return ((0xff & b[i]) << 24) |
            ((0xff & b[i + 1]) << 16) |
            ((0xff & b[i + 2]) << 8) |
            ((0xff & b[i + 3]));
    };

    export function getAtp(value: number, volume: number, trade_value: number, market_lot: number, market_id: string) {
        if (market_id === MarketKeywords.NSECDS || market_id === MarketKeywords.BSECDS) {
            return value / (volume * market_lot * trade_value);
        }
        return value / (volume * trade_value);
    }

    export function convertOHLC(data: OhlcDataResponse, scrip_precision: number, trade_value: number, market_lot: number, market_id: string, symbol_id: number) {
        const chartingRes: ChartDataResponse[] = [];
        data.getOhlcvListList().forEach(el => {
            chartingRes.push({
                timestamp: el.getTimestamp() * 1000,
                open_price: applyPrecision(el.getOpen(), scrip_precision),
                close_price: applyPrecision(el.getClose(), scrip_precision),
                high_price: applyPrecision(el.getHigh(), scrip_precision),
                low_price: applyPrecision(el.getLow(), scrip_precision),
                volume: el.getVolume(),
                value: data.getIsMarketMovementResponse() ? applyPrecision(el.getValue(), scrip_precision) : 0,
                symbol_id: symbol_id,
                atp: data.getIsMarketMovementResponse() ?
                    applyPrecision(getAtp(el.getValue(), el.getVolume(),
                        trade_value, market_lot, market_id), scrip_precision) : 0
            });
        });
        return chartingRes;
    }

    export function concatArrayBuffers(...bufs: Uint8Array[]): Uint8Array {
        var offset = 0;
        var bytes = 0;
        var bufs2 = bufs.map((buf) => {
            bytes += buf.byteLength;
            return buf;
        });
        var buffer = new ArrayBuffer(bytes);
        var store = new Uint8Array(buffer);
        bufs2.forEach(function (buf) {
            store.set(buf, offset);
            offset += buf.byteLength;
        });
        return store

    }

    export function getStrikeOffset(strikePrice: String) {
        let strikeVal = 0;
        const split = strikePrice.trim().split(/\s+/);
        if (split.length > 1) {
            strikeVal = parseFloat(split[2]) * 100;
            if (split[1] == '-') {
                strikeVal = -Math.abs(strikeVal);
            }
        }

        return strikeVal;
    }

    export function validateIndicatorValue(indicatorB: boolean) {
        return (control: AbstractControl): ValidationErrors | null => {
            if (control.value === '-' && indicatorB) {
                return { 'invalidValue': true };
            }
            return null;
        }
    }

    export function startEndDateValidator(
        fromDateField: string, toDateField: string,
        fromTimeField: string, toTimeField: string,
        currentDayAllowed: boolean,
        minTime?: string, maxTime?: string, isBacktest = false, strategyMode = 'Regular'
    ): ValidatorFn {
        return (formGroup: AbstractControl): { [key: string]: boolean } | null => {
            if (strategyMode === 'Wizard' || isBacktest) {
                const invalidDate = "Invalid date";
                const fromDate = formGroup.get(fromDateField).value;
                const toDate = formGroup.get(toDateField).value;
                const fromTimeMoment = moment(formGroup.get(fromTimeField).value);
                const fromTime = fromTimeMoment.format(TIME_FORMAT);
                const fromDateMoment = moment(fromDate).set('hour', fromTimeMoment.get('hour')).set('minute', fromTimeMoment.get('minutes'));

                const todayMidnight = moment({
                    hours: 0,
                    minutes: 0,
                    seconds: 0
                });

                const toTime = moment(formGroup.get(toTimeField).value).format(TIME_FORMAT);
                if (fromDate && toDate && moment(fromDate).isAfter(moment(toDate))) {
                    return { DateError: true };
                }


                // From date should not be less that current date. (Not in backtest)
                if (!isBacktest && !currentDayAllowed && moment(fromDate).isBefore(moment({
                    hours: 0,
                    minute: 0,
                    second: 0
                })) ) {
                    return { StartDateError: true };
                }

                /*
                  * if start date is less than end date,
                  * checking if end date is less than current date
                  * Example:
                  * startDate = 02-08-2021 , EndDate = 04-08-2021
                  * so error will be displayed for end date.
                  * if end date is same as current date , checking for end time .

                */
                if (fromDate && toDate && moment(fromDate).isSameOrBefore(moment(toDate))) {
                    if (!isBacktest && new Date(toDate).getTime() < todayMidnight.unix()) {
                        return { EndDateError: true };
                    }
                    if (!isBacktest && (new Date(toDate).getTime() === todayMidnight.unix()) && (toTime < moment().format(TIME_FORMAT))) {
                        return { CurrentTimeError: true };
                    }
                    if (!isBacktest && (toTime < fromTime)) {
                        return { TimeError: true };
                    }
                }
                if (minTime && maxTime) {
                    if (fromTime !== invalidDate && fromTime < minTime) {
                        return { RangeMinError: true };
                    }
                    if (toTime !== invalidDate && maxTime < toTime) {
                        return { RangeMaxError: true };
                    }
                }
                return null;
            }
        };
    }

    export function parseTime(t: string) {
        var d = new Date();
        var time = t.match(/(\d+)(?::(\d\d))?\s*(p?)/);
        d.setHours(parseInt(time[1]) + (time[3] ? 12 : 0));
        d.setMinutes(parseInt(time[2]) || 0);
        return d;
    }

    export function formatTime(date: Date, format = MOMENT_COMPLETE_TIME) {
        return moment(date).format(format);
    }

    /**
     *  Converts integer time to moment object
     *
     * @param {number} time time in form of integer
     * @returns {moment object} moment date time object
     */
    export function standardiseTime(time: number) {
        if (!time) {
            return null;
        }
        let timeStr = time.toString();
        if (timeStr.length === 5) {
            return moment(time, 'hmmss');
        }
        return moment(time, 'hhmmss');
    }

    export function formatTags(val: string[]) {
        return val?.length ? val.join(' , ') : null;
    }

    export function stringToEpoch(value: string | number) {
        return moment(value).unix();
    }

    export function snakeCaseToTitleCase(str: string) {
        return str.split('_').map(s => s[0].toUpperCase() + s.slice(1)).join(' ');
    }

    export function calculateNetROIPercent(
        totalProfit: number,
        totalMargin: number,
        noOfDays: number
    ): number {
        if (totalProfit <= 0 || totalMargin <= 0) return 0;
        return (totalProfit / (totalMargin / noOfDays)) * 100;
    }

    export function calculateDayROIPercent(
        totalProfit: number,
        totalMargin: number
    ): number {
        if (totalProfit <= 0 || totalMargin <= 0) return 0;
        return parseFloat(((totalProfit / totalMargin) * 100).toFixed(DEFAULT_PRECISION));
    }

    export function calculatePercentageValue(percentage: string, value: number) {
        let percent = parseInt(percentage) / 100;
        return Math.round(percent * value);
    }

    export function sortAlertsOnTime(a, b) {
        if (a.alert_time < b.alert_time) {
            return 1;
        }
        if (a.alert_time > b.alert_time) {
            return -1;
        }
        return 0;
    }

    export function createStoreObject(data: any) {
        const newData: subscribeQuoteParams = {
            market_id: data.market_id ,
            security_id: data.security_id,
            symbol_id: data.symbol_id,
            scrip_precision: data.scrip_precision ? data.scrip_precision : 2
        };
        return newData;
    }

        /**
     * Sort an array by a key in increasing as well as decreasing order.
     */
        export function sortByKey(key: string, reverse: SortingTypes) {
            return (a, b) => {
                if (reverse === SortingTypes.Descending) {
                    return (a[key] < b[key]) ? 1 : -1;
                } else if (reverse === SortingTypes.Ascending) {
                    return (a[key] > b[key]) ? 1 : -1;
                }
            };
        }

    /**
     * @param searchResult data based on user selected symbol
     * @param strikePriceOffset it has either ATM or basic
     * @param expiry expiry date selected by user.
     * @returns returns either ATM range list or strike prices list based
     * on the strikePriceOffset sent.
     */
    export function extractStrikePricesList(searchResult: SearchSymbolResults[], strikePriceOffset: StrikePriceFilter, expiry = '') {
        switch (strikePriceOffset) {
            case StrikePriceFilter.ATM:
                /**
                 * getting first expiry date from serach results
                 * filtering strike prices for particular expiry data and
                 * sorting them in ascending order.
                 */
                const expiryDay = searchResult[0].expiry;
                let result = searchResult.filter(el => el.expiry == expiryDay).map(r => +r.strike_price).
                    filter((v, i, a) => a.indexOf(v) === i);
                result.sort(function (a, b) { return a - b });
                const atm_offset = getStepSize(searchResult);
                let positiveRange = [];
                let negativeRange = [];
                for (let i = 1; i <= ATM_RANGE; i++) {
                    negativeRange.push(getAtmData('-', atm_offset * i));
                    positiveRange.push(getAtmData('+', atm_offset * i));
                }
                negativeRange = negativeRange.reverse();
                const finalData = [...negativeRange, 'ATM', ...positiveRange];
                return finalData;
            case StrikePriceFilter.basic:
                if (expiry) {
                    searchResult = searchResult.filter(el => el.expiry === expiry)
                }
                return searchResult.map(r => r.strike_price).sort(function (a, b) { return +a - +b });
        }
    }

    export function parseAtmString(strikePrice: string) {
        let strikeVal = 0;
        const split = strikePrice.trim().split(/\s+/);
        if (split.length > 1) {
            strikeVal = parseFloat(split[2]);
            if (split[1] == '-') {
                strikeVal = -Math.abs(strikeVal);
            }
        }

        return strikeVal;
    }

    export function getStepSize(searchResult: SearchSymbolResults[]) {
        /**
                 * getting first expiry date from serach results
                 * filtering strike prices for particular expiry data and
                 * sorting them in ascending order.
                 */
        const expiryDay = searchResult[0].expiry;
        let result = searchResult.filter(el => el.expiry == expiryDay).map(r => +r.strike_price).
            filter((v, i, a) => a.indexOf(v) === i);
        result.sort(function (a, b) { return a - b });

        /**
         * finding difference between the elements in the array
         * later comparing the diff with minDiff and updating
         * minDiff accordingly
         * assigning minDiff to offset.
         */
        let minDiff = Number.MAX_SAFE_INTEGER;
        for (let i = 1; i < result.length; i++) {
            const diff = Math.abs(result[i] - result[i - 1]);
            if (diff < minDiff) {
                minDiff = diff;
            }
        }

        return minDiff;
    }

    /**
     * function used to filter search results based on expiry date
     * falling between startDate and endDate
     * startDate will be start date of the week or month based on the filter
     * endDate will be last date of the week or month based on the filter
     */
    export function getFilteredSearchResults(searchResult: SearchSymbolResults[], momObj: moment.Moment,
        filter: moment.unitOfTime.StartOf) {
        const startDate = momObj.clone().startOf(filter);
        const endDate = momObj.clone().endOf(filter);

        return searchResult.filter(function (el) {
            const currentExpiry = moment(new Date(el.expiry));
            currentExpiry.set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
            return currentExpiry.isBetween(startDate, endDate);
        });

    }

    export function sortExpiry(a, b) {
        const aExpiry = moment(a.expiry, MOMENT_OPTION_FORMAT);
        const bExpiry = moment(b.expiry, MOMENT_OPTION_FORMAT);
        if (aExpiry < bExpiry) {
            return -1;
        }
        if (aExpiry > bExpiry) {

            return 1;
        }
        return 0;
    }


    export function sortOnTimeStamps(a, b) {
        return a['time'] < b['time'] ? -1 : 1;
    }

    export function getAtmData(operator: string, num: number) {
        return 'ATM ' + operator + ' ' + num
    }

    /**
     * Function that converts timestamp to seconds.
     * (Better approach can be implemented)
     * @param timestamp number to be converted
     */
    export function manageTimestamp(timestamp: number) {
        const length = timestamp.toString().length;
        if (length === 10) {
            return timestamp * 1e3;
        } else if (length === 16) {
            return timestamp * 1e-3;
        }
        return timestamp;
    }

    export function milliToSeconds(time: number) {
        return Math.floor(time / 1e3);
    }

    export function hexColorDelta(arr: string[], hex2: string) {

        let arrResults = [];
        arr.forEach(el => {
            // get red/green/blue int values of hex1
            var r1 = parseInt(el.substring(1, 3), 16);
            var g1 = parseInt(el.substring(3, 5), 16);
            var b1 = parseInt(el.substring(5, 7), 16);

            // get red/green/blue int values of hex2
            var r2 = parseInt(hex2.substring(1, 3), 16);
            var g2 = parseInt(hex2.substring(3, 5), 16);
            var b2 = parseInt(hex2.substring(5, 7), 16);

            // calculate differences between reds, greens and blues
            var r = 255 - Math.abs(r1 - r2);
            var g = 255 - Math.abs(g1 - g2);
            var b = 255 - Math.abs(b1 - b2);

            // limit differences between 0 and 1
            r /= 255;
            g /= 255;
            b /= 255;
            // 0 means opposit colors, 1 means same colors
            arrResults.push(((r + g + b) / 3));
        });


        return Math.max(...arrResults);
    }

    /**
     * This utility removes undefined fields from any 'Object' inherited
     * structure.
     *
     * Example ->    Input                      Output
     *               {                          {
     *                 field1: 'Val',              field1: 'Val'
     *                 field2: undefined|null   }
     *               }
     *
     * @export
     * @template T Any Object Inherited Structure
     * @param {T} originalObject Original value
     * @returns Value without any undefined values.
     */
    export function removeUndefined<T extends Object>(originalObject: T) {
        return pickBy(originalObject, identity => true);
    }

    /**
        * Function to apply different style to second word of string.
      */
    export function styleSecondWord(value: string, classToApply: string) {
        value = '<span>' + value.replace(/\w+ (\w+)/, function (s, c) {
            return s.replace(c, `<span class="${classToApply}">${c}</span>`) + '</span>';
        });

        return value;
    }


    export function getClass(value: string) {
        if (value && (value.toString().charAt(value.length - 1) === '%')) {
            value = value.slice(0, -1);
        }
        if (+value > 0) {
            return 'text-positive';
        } else if (+value < 0) {
            return 'text-negative';
        }
    }


    export function getDiscountedPrice(price, discount) {
        return getCharmPrice(price - price * (discount / 100));
    }

    // Rounds off to nearest 9s
    export function getCharmPrice(price) {
        const ret = (Math.ceil(price / 10) * 10);
        if (ret === 0) {
            return 0
        }
        return ret - 1;
    }

    export function getOrderStatusColorClass(value: string) {
        if (value == OrderStatus.partially_filled) {
            return 'star-color';
        } else if (value == OrderStatus.filled) {
            return 'text-positive';
        } else if (REJECTED_ORDERS.some(status => status === value)) {
            return 'text-negative';
        } else {
            return 'fg-accent';
        }
    }

    export function getStatusClass(status: number) {
        if (status === PortfolioShareStatus.PORTFOLIOSHARESTATUS_PENDING) {
            return 'status-pending';
        } else if (status === PortfolioShareStatus.PORTFOLIOSHARESTATUS_ACCEPTED) {
            return 'status-accepted';
        } else if (status === PortfolioShareStatus.PORTFOLIOSHARESTATUS_REJECTED) {
            return 'status-rejected';
        } else if (status === PortfolioShareStatus.PORTFOLIOSHARESTATUS_REVOKED) {
            return 'status-revoked';
        }
        return '';
    }

    export namespace FormValidations {

        export const VALIDATION_ERRORS = ['compErr', 'TargetProfitError', 'StopLossError', 'LockProfitError', 'TrailByError']

        export function checkPasswords(group: UntypedFormGroup) {
            const oldPass = group.controls.oldPassword.value;
            const pass = group.controls.newPassword.value;
            if (oldPass == pass) {
                return { sameNewOld: true };
            }
            const confirmPass = group.controls.confirmNewPassword.value;
            return pass === confirmPass ? null : { notSame: true };

        }
        export function checkPin(group: UntypedFormGroup) {
            const pin = group.controls.mpin.value;
            const confirmPin = group.controls.confirmMpin.value;
            const oldPin = group.controls.oldMPin.value;
            if (pin === oldPin) {
                return { sameNewOld: true };
            }
            return pin === confirmPin ? null : { notSame: true };
        }

        export function getColumnData(dataSource: any, columnName: string) {
            const retArray = [];
            dataSource.forEach(element => {
                retArray.push(element[columnName]);
            });
            return retArray;
        }

        export function checkPriceFields(group: UntypedFormGroup) {
            const priceType = group.controls.priceType.value;
            if (priceType === PriceType.LIMIT || priceType === PriceType.STOP_LOSS_LIMIT) {
                const price = group.controls.price.value;
                return price !== null ? null : { priceRequired: true };
            }
        }

        export function getFormValidationErrors(control: UntypedFormGroup) {
            const errors = [];
            Object.keys(control.controls).some((key) => {

                const controlErrors: ValidationErrors = control.get(key).errors;
                if (controlErrors != null) {
                    Object.keys(controlErrors).some((keyError) => {
                        console.log('Key control: ' + key + ', keyError: ' + keyError + ', err value: ', controlErrors[keyError]);
                        errors.push('Enter a valid ' + toSentence(key));
                        return true;
                    });
                    return true;
                }
            });
            return errors;
        }

        export function comparatorValidator(control1?: string, control2?: string, errorName: string = VALIDATION_ERRORS[0], control3?: string, exitForm = false): ValidatorFn {
            return (group: UntypedFormGroup) => {
                const first = group.get(control1);
                const second = group.get(control2);
                const third = group.get(control3);

                const firstValue = VALIDATION_ERRORS.includes(errorName) ? parseFloat(first.value) : first.value;
                const secondValue = VALIDATION_ERRORS.includes(errorName) ? parseFloat(second.value) : second.value;

                if (first && second && first.value && first.enabled && second.enabled && firstValue > secondValue) {
                    if (exitForm && third?.value === MtmCheckType.MTMCHECKTYPE_UNDERLYING_POINTS.toString()) {
                        return null;
                    }
                    if (!exitForm && third?.value === LegExitTypes.LEGEXITTYPES_UNDERLYING_POINTS.toString()) {
                        return null
                    }
                    const obj = {};
                    obj[errorName] = true;
                    return obj;
                }
                return null;
            }
        }

        // Need to show error for inputs where At Least one input is required to fill between two
        export function atLeastOneRequiredValidator(control1?: string, control2?: string, errorName: string = VALIDATION_ERRORS[0]): ValidatorFn {
            return (control: AbstractControl): { [key: string]: any } | null => {
                const first = control.get(control1);
                const second = control.get(control2);

                if (!first.value && !second.value && first.enabled && second.enabled) {
                    const obj = {};
                    obj[errorName] = true;
                    return obj;
                }
                return null;
            };
        }

        export function comparatorValidatorForTime(control1?: string, control2?: string, errorName: string = VALIDATION_ERRORS[0]): ValidatorFn {
            return (control: AbstractControl): { [key: string]: any } | null => {
                const first = control.get(control1);
                const second = control.get(control2);
                if (first && second && first.value && second.value) {
                    const firstValue = parseInt(moment(first.value).format(HFT_TIME_FORMAT));
                    const secondValue = parseInt(moment(second.value).format(HFT_TIME_FORMAT));
                    if (firstValue > secondValue) {
                        const obj = {};
                        obj[errorName] = true;
                        return obj;
                    }
                    return null;
                }
            };
        }


        export function symbolValidator(controlRef: AbstractControl, searchedSymbols: any) {
            const found = find(searchedSymbols, (symbol) => {
                return symbol.symbol === controlRef.value;
            });

            if (!found) {
                controlRef.setValue('');
                return false;
            }

            return true;
        }

        /**
         * Function to Convert Camel Case  to Normal Sentences.
         */
        export function toSentence(str) {
            const result = str.replace(/([A-Z])/g, ' $1').replace(/^./, (replacedStr) => replacedStr.toUpperCase());
            return result;
        }

        /**
         * Validates date input and clears form-level validators when individual field is invalid
         * @param event - The input event
         * @param controlName - The name of the form control
         * @param formGroup - The form group containing the control
         * @param datePattern - The date pattern to validate the input
         */
        export function onDateInput(event: any, controlName: string, formGroup: UntypedFormGroup, datePattern: RegExp) {
            const input = event.target.value.trim();
            const control = formGroup.get(controlName);
            
            if (input && !datePattern.test(input)) {
                control.setErrors({ invalidDate: true });
                return;
            }
            
            // If pattern matches, validate if it's a real date
            if (input && datePattern.test(input)) {
                const [month, day, year] = input.split('/');
                const date = new Date(+year, +month - 1, +day);
                
                // Check if the date is valid (not NaN and matches input)
                if (isNaN(date.getTime()) || 
                    date.getMonth() !== +month - 1 || 
                    date.getDate() !== +day || 
                    date.getFullYear() !== +year) {
                    control.setErrors({ invalidDate: true });
                    return;
                }
                
                // Clear errors if date is valid
                if (control.hasError('pattern') || control.hasError('invalidDate')) {
                    control.setErrors(null);
                }
            }
        }
    }

    // sorting based on lowercase
    export function applySortingToLowerCase(item: any, property: string) {
        if (typeof item[property] === 'string' && item[property]) {
            return item[property].toLowerCase();
        }
        return item[property];
    }

    // Find GCD of two numbers
    export function gcd(a: number, b: number) {
        let temp: number;
        while (b > 0) {
            temp = b;
            b = a % b;
            a = temp;
        }
        return a;
    }

    //bold the string
    export function boldText(val: string = '') {
        return `<strong>${val}</strong>`;
    }

    // Calculate GCD of all elements in an array
    export function findGCD(arr) {
        let result = arr[0];
        for (let i = 1; i < arr.length; i++) {
            result = gcd(arr[i], result);
            // If any lots has gcd 1 then return 1
            if (result == 1) { return 1; }
        }
        return result;
    }

    // This function will return the difference of two objects
    export function getObjectDifference(obj1: object, obj2: object) {
        let keyFound = {};
        Object.keys(obj1).forEach(key => {
            // Avoiding nested objects for now as it will add complexity in logic and also not required for now.
            if (obj1[key] !== obj2[key] && ((typeof (obj1[key]) !== 'object' || obj1[key] === null)
                && (typeof (obj2[key]) !== 'object' || obj2[key] === null))) {
                keyFound[key] = obj2[key];
            }
        });
        return keyFound;

    }

    export function portfolioStateButton(type: string, portfolio: BriefPortfolioModel, isCreator: boolean, autoSquareOff: boolean = false, isAcceptedPortfolioView: boolean = true) {
        switch (type) {
            case MarketPlaceCardsType.Originals:
            case MarketPlaceCardsType.Bookmarked:
            case MyStrategyType.AiRecommended:
            case MyStrategyType.Saved:
                return allButtonState(portfolio, autoSquareOff);

            case MarketPlaceCardsType.Created:
                return allButtonState(portfolio, autoSquareOff);

            case MyStrategyType.Deployed:
                if (portfolio.terminated && portfolio.isPositionAvailable) {
                    const buttonState = autoSquareOff ? ButtonStateType.CancelAutoSquareOff : ButtonStateType.SquareOff;
                    return buttonState;
                } else if (portfolio.forceTerminated) {
                    return null;
                } else if (!portfolio.terminated) {
                    return MyButtonType.Undeploy;
                } else {
                    return null;
                }

            case MyStrategyType.Shared:
                if (isAcceptedPortfolioView) {
                    return allButtonState(portfolio, autoSquareOff);
                } else {
                    return ButtonStateType.Accept
                }

            default:
                return ButtonStateType.ErrorState;
        }
    }

    export function allButtonState(portfolio, autoSquareOff) {
        if (portfolio.terminated && portfolio.isPositionAvailable) {
            const buttonState = autoSquareOff ? ButtonStateType.CancelAutoSquareOff : ButtonStateType.SquareOff;
            return buttonState;
        } else {
            const buttonState = portfolio.deployed ? MyButtonType.Undeploy : MyButtonType.Deploy;
            return buttonState;
        }
    }

    export function calculateMonthlyEarnings(data: NetUserProfit[]): number {
        // Convert fromDate and toDate to Date objects for comparison
        const to = new Date();
        const from = new Date(to.getFullYear(), to.getMonth(), 1);

        // Filter the data to include only the dates within the range and sum the earnings
        const totalEarnings = data.reduce((sum, item) => {
            const currentDate = new Date(item.created_date);
            if (currentDate >= from && currentDate <= to) {
                return sum + item.earnings;
            }
            return sum;
        }, 0);

        return totalEarnings;
    }

    export function getUint64(dataview, byteOffset, littleEndian) {
        // split 64-bit number into two 32-bit (4-byte) parts
        const left = dataview.getUint32(byteOffset, littleEndian);
        const right = dataview.getUint32(byteOffset + 4, littleEndian);

        // combine the two 32-bit values
        const combined = littleEndian ? left + 2 ** 32 * right : 2 ** 32 * left + right;

        if (!Number.isSafeInteger(combined))
            console.warn(combined, 'exceeds MAX_SAFE_INTEGER. Precision may be lost');

        return combined;
    }

    export function getJoinedString(...args) {
        return args.join('_');
    }

    export function breakJoinedString(joined: string): string[] {
        return joined.split('_')
    }

    /**
     * Converts index quote to symbol quote.
     */
    export function indexToSymbolQuote(quote: SingleIndexQuotes) {
        quote['last_trade_price'] = quote.LastTradePrice;
        quote['last_update_time'] = quote.LastTradeTime;
        quote['close_price'] = quote.ClosePrice;
        quote['market_id'] = quote.ExchangeId;
        quote['security_id'] = quote.SecurityId;

        // Open, high, low values doesn't come in live index quotes, so old values shouldn't be changed unecessarily.
        if ((quote as SingleIndexQuotes).OpenPrice) {
            quote['open_price'] = (quote as SingleIndexQuotes).OpenPrice;
            quote['high_price'] = (quote as SingleIndexQuotes).HighPrice;
            quote['low_price'] = (quote as SingleIndexQuotes).LowPrice;
        }

        return quote;
    }

    /**
     * This utility add precision
     *
     * @param {value} number value
     * @param {precision} number precision
     * @param {applyFixed} boolean If true then then apply precision with toFixed
     * @returns Value after applying precision.
     */
    export function precisionUtil(value: number, precision: number, applyFixed = true) {

        if (value && !isNaN(value)) {
            value = +value;
        }

        if (typeof (value) === 'number') {
            const zero = 0;
            if (Math.abs(value) < Math.pow(10, -precision)) {
                return zero.toFixed(precision);
            }
            let val = (value / Math.pow(10, precision));

            if (applyFixed) {
                return val.toFixed(precision);
            }
            return (val.toString());
        }
    }

    export function processDateTime(dateObj: Date, timeObj: Date) {
        const tempDate = dateObj;
        const dateM = moment(
            `${tempDate.toDateString()} ${timeObj.getHours()}:${timeObj.getMinutes()}:0`
        ).utcOffset(0, true).unix();
        return dateM;
    }

    /**
     * This utility return execution days string
     *
     * @param {executeDays} number[]
     * @returns execution days string
     */
    export function getExecutionDays(executionDaysParams: executionDaysParams) {
        // If execution days type is week days then return days else return days to expiry
        if (executionDaysParams?.executionDaysType === ExecutionDaysType.EXECUTIONDAYSTYPE_WEEK_DAYS) {
            return executionDaysParams?.executeDays?.includes(ExitExecutionDays.EXITEXECUTIONDAYS_ALL) ? "All" : executionDaysParams.executeDays.map(day => ALLOWED_STRATEGY_DAYS[day - 1]).join(',');
        } else {
            return Utils.executionDaysOnCard(executionDaysParams?.operatorType, executionDaysParams?.daysToExpiry);
        }
    }


    /**
     * Checks if today's day is included in the given array of days.
     * @param days - An array of numbers representing the days of the week (0-6, where 0 is Sunday).
     * @returns A boolean value indicating whether today's day is included in the array.
     */
    export function isTodayIncluded(days: number[], type): boolean {
        //strategy need not be shown in deployed section if day is different
        if (type === MyStrategyType.Deployed) {
            const today = new Date().getDay();
            return days.includes(today);
        }
        return true;
    }

    // Function to Compress Images
    export async function compressImage(imageFile, maxWidth = 100) {
        const options: Options = {
            maxWidthOrHeight: maxWidth
        }
        try {
            return await imageCompression(imageFile, options);
        } catch (err) {
            console.log('Error in image compression', err);
            return;
        }

    }

    // Function to check current time is before exchange end time
    export function isBeforeCurrentTime(time: string, timeFormat: string) {
        const endTime = moment(time, timeFormat);
        return moment().isBefore(endTime);
    }

    export function formatValues(value) {
        const val = Math.abs(value);
        if (val >= 1000000) {
            return (value / 1000000) + 'M';
        } else if (val >= 100000) {
            return (value / 100000) + 'L';
        } else if (val >= 1000) {
            return (value / 1000) + 'K';
        } else {
            return value;
        }
    }

    export function makeCustomOrder(Order: number[], values: any) {
        const customOrderOptions = Order.map(item => (
            {
                key: item.toString(),
                value: values[item],
            }))
        return customOrderOptions;
    }

    export function getMessageString(originalPortfolioName: string) {
        if (originalPortfolioName) {
            return `Please note the original portfolio <strong>${originalPortfolioName}</strong> has been updated. `;
        }
        return '';
    }

    export function getSelectedDays(array) {
        let string = ""
        array.forEach((day) => {
            if (day !== 0) {
                string += EXIT_EXECUTION_DAYS_VALUES[day.toString()] + ", ";
            }
        })
        return string.substring(0, string.length - 2) + "<br>";
    }

    export function getCurrentDate(dateString: string, dateStringType: string) {
        const date = moment(dateString, dateStringType);
        return moment().set({ hours: date.hours(), minutes: date.minutes(), seconds: date.seconds() }).toDate();
    }

    // A form can be invalid in two ways either mandatory fields are missing or value is invalid.This function checks any
    // field in form is invalid or not by checking invalid values not mandatory fields.

    export function isAnyFieldInvalid(form: UntypedFormGroup) {
        if (form.errors && !form.errors['TargetProfitRequired']) {
            return true;
        }
        for (const controlName in form.controls) {
            const control = form.get(controlName);
            if (control instanceof UntypedFormGroup) {  //if control is itself a form
                if (checkValidityInControl(control)) {
                    return true;
                }
            }

            if (control.errors && !control.errors.required && !control.errors['TargetProfitRequired']) {
                return true;
            }
        }
        return false;
    }

    export function extractDaysOfHolidays(holidays: any) {
        const days = [];
        Object.keys(holidays).forEach(holiday => {
            let datePart = holiday.split('_');
            days.push(datePart[0]);
        });
        return days;
    }

    function checkValidityInControl(form: UntypedFormGroup) {
        if (form.errors && !form.errors['TargetProfitRequired']) {
            return true;
        }

        for (const controlName in form.controls) {
            const ctr = form.get(controlName);
            if (ctr.errors && !ctr.errors.required && !ctr.errors['TargetProfitRequired']) {
                return true;
            }
        }
    }

    export function minTimeValidator(minTime, maxTime, isSuperAdminValidity): ValidatorFn {
        minTime = parseInt(moment(minTime).format(HFT_TIME_FORMAT));
        maxTime = parseInt(moment(maxTime).format(HFT_TIME_FORMAT));

        return (control: AbstractControl): { [key: string]: boolean } | null => {
            {
                const value = parseInt(moment(control.value).format(HFT_TIME_FORMAT));
                if (value < minTime && isSuperAdminValidity) {
                    return { 'minTime': true };
                } else if (value < minTime && !isSuperAdminValidity) {
                    return { 'minTimeUser': true };
                }
                if (value > maxTime && isSuperAdminValidity) {
                    return { 'maxTime': true };
                } else if (value > maxTime && !isSuperAdminValidity) {
                    return { 'maxTimeUser': true };
                }
                return null;
            }
        }
    }

    export function valueComparatorValidator(minValue: number, errorKey: string , isPortfolio: boolean = true): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const value = parseInt(moment(control.value).format(HFT_TIME_FORMAT));
            if (value > minValue && isPortfolio) {
                return { [errorKey]: true };
            } else if (value < minValue && !isPortfolio) {
                return { [errorKey]: true };
            }
            return null;
        }
    }
    


    export function emailValidator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const invalidEmail = { invalidEmail: true };
            const email = control.value?.toLowerCase();
            if (!email) return {};

            var emailParts = email.split('@');

            if (emailParts.length !== 2) return invalidEmail;

            var account = emailParts[0];
            var address = emailParts[1];

            if (account.length > 64 || address.length > 255) return invalidEmail;

            var domainParts = address.split('.');

            // If any part of the domain is longer than 63 characters, the email is invalid.
            if (domainParts.some(function (part) {
                return part.length > 63;
            })) return invalidEmail;

            const tester = RegExp(REG_EX.email)

            return tester.test(email) ? null : invalidEmail;
        }
    }

    export function getAllowedExchanges(res, exchangeStrings: any = EXCHANGE_STRINGS) {
        const permissions = res.plan.algo_permissions;
        const exchanges = Object.assign({}, exchangeStrings);
        Object.keys(exchangesKeys).forEach(key => {
            if (!permissions.includes(parseInt(key))) {
                delete exchanges[exchangesKeys[key]];
            }
        });
        return exchanges;
    }

    export function executionDaysOnCard(operatorType, daysToExpiry) {
        return "DTE " + COMPARATORS_STRING[operatorType] + " " + daysToExpiry;
    }

    export function isBSESelected(exchange) {
        return (exchange === ExchangeId.EXCHANGEID_BSECM.toString() || exchange === ExchangeId.EXCHANGEID_BSEFO.toString());
    }

    export function getSelectedExchanges(exchange) {
        return (exchange === ExchangeId.EXCHANGEID_NSECM.toString() || exchange === ExchangeId.EXCHANGEID_BSECM.toString());
    }

    export function isCMSelected(exchange) {
        return (exchange === EXCHANGE_STRINGS[ExchangeId.EXCHANGEID_NSECM] || exchange === EXCHANGE_STRINGS[ExchangeId.EXCHANGEID_BSECM]);
    }

    export function checkForExchangeBacktest(exchange: number, backtestPermissions: BacktestPermissions) {
        //NSECM and BSECM are added if required in future
        if (!backtestPermissions) {
            return false
        }
        switch (exchange) {
            case ExchangeId.EXCHANGEID_NSEFO:
                return backtestPermissions.NSEFO_BACKTEST;

            case ExchangeId.EXCHANGEID_BSEFO:
                return backtestPermissions.BSEFO_BACKTEST;

            case ExchangeId.EXCHANGEID_NSECM:
                return backtestPermissions.NSECM_BACKTEST;

            case ExchangeId.EXCHANGEID_BSECM:
                return backtestPermissions.BSECM_BACKTEST;

            default:
                return backtestPermissions.NSEFO_BACKTEST;
        }

    }

    /**
     * Retrieves the symbols allowed for a given plan according to permissions.
     * @param plan - The plan details.
     * @returns A map containing the symbols for each exchange.
     */
    export function getSymbolsForPlan(plan: PlanDetailI | CatalogPlan | UserPlan) {
        let permissions = plan.algo_permissions;
        let nseSymbols = [];
        let bseSymbols = [];
        let nsecmAllEquityExist: boolean = false;
        let nsecmAllIndicesExist: boolean = false;
        let bsecmAllIndicesExist: boolean = false;
        let bsecmAllEquityExist: boolean = false;

        permissions.forEach(per => {
            switch (per) {
                case AlgoPermissions.WEBALGOPERMISSION_NSECM:
                    nsecmAllEquityExist = true;
                    if (nsecmAllIndicesExist) {
                        nseSymbols = [SymbolsSelected.All];
                    }
                    break;
                case AlgoPermissions.WEBALGOPERMISSION_ALLNSESYMBOLS:
                    nsecmAllIndicesExist = true;
                    if (nsecmAllEquityExist) {
                        nseSymbols = [SymbolsSelected.All];
                    } else {
                        nseSymbols = [SymbolsSelected.All_FO];
                    }
                    break;
                case AlgoPermissions.WEBALGOPERMISSION_BSECM:
                    bsecmAllEquityExist = true;
                    if (bsecmAllIndicesExist) {
                        bseSymbols = [SymbolsSelected.All];
                    }
                    break;
                case AlgoPermissions.WEBALGOPERMISSION_ALLBSESYMBOLS:
                    bsecmAllIndicesExist = true;
                    if (bsecmAllEquityExist) {
                        bseSymbols = [SymbolsSelected.All];
                    } else {
                        bseSymbols = [SymbolsSelected.All_FO];
                    }
                    break;
                case AlgoPermissions.WEBALGOPERMISSION_NIFTY:
                    !nsecmAllIndicesExist && nseSymbols.push(ExchangePermissionMappingRev[AlgoPermissions.WEBALGOPERMISSION_NIFTY]);
                    break;

                case AlgoPermissions.WEBALGOPERMISSION_BANKNIFTY:
                    !nsecmAllIndicesExist && nseSymbols.push(ExchangePermissionMappingRev[AlgoPermissions.WEBALGOPERMISSION_BANKNIFTY]);
                    break;

                case AlgoPermissions.WEBALGOPERMISSION_FINNIFTY:
                    !nsecmAllIndicesExist && nseSymbols.push(ExchangePermissionMappingRev[AlgoPermissions.WEBALGOPERMISSION_FINNIFTY]);
                    break;

                case AlgoPermissions.WEBALGOPERMISSION_MIDCAPNIFTY:
                    !nsecmAllIndicesExist && nseSymbols.push(ExchangePermissionMappingRev[AlgoPermissions.WEBALGOPERMISSION_MIDCAPNIFTY]);
                    break;
                case AlgoPermissions.WEBALGOPERMISSION_SENSEX:
                    !bsecmAllIndicesExist && bseSymbols.push(ExchangePermissionMappingRev[AlgoPermissions.WEBALGOPERMISSION_SENSEX]);
                    break;

                case AlgoPermissions.WEBALGOPERMISSION_BANKEX:
                    !bsecmAllIndicesExist && bseSymbols.push(ExchangePermissionMappingRev[AlgoPermissions.WEBALGOPERMISSION_BANKEX]);
                    break;
            }
        })

        if (nsecmAllEquityExist && !nsecmAllIndicesExist) {
            nseSymbols.push(SymbolsSelected.ALL_EQUITY);
        }

        if (bsecmAllEquityExist && !bsecmAllIndicesExist) {
            bseSymbols.push(SymbolsSelected.ALL_EQUITY);
        }

        let symbolsMap: Map<Exchange, string> = new Map();
        symbolsMap.set(Exchange.NSE, nseSymbols.join(', '));
        symbolsMap.set(Exchange.BSE, bseSymbols.join(', '));
        return symbolsMap;
    }

    export function withdrawMarginLimit(marginSummary: MarginResponse) {
        const withdrawAmount = ((marginSummary.cash_available) - (Math.max(marginSummary.adhoc, 0) +
            Math.max(marginSummary.custom_component, 0) + marginSummary.collateral +
            marginSummary.osp_credited + marginSummary.credit_for_sale + marginSummary.delivery_sell_benefit));

        // if withdrawAmount is negative then will show as zero
        return withdrawAmount < 0 ? 0 : withdrawAmount;
    }

    export function expiryTypeValidator(expiryTypes: string[]): ValidatorFn {
        return (control: AbstractControl): { [key: string]: boolean } | null => {
            {
                if (!expiryTypes.includes(control.value)) {
                    return { 'expiryTypeError': true };
                }
            }
        }
    }

    export function CalOneMinute(date: Date, add: boolean): Date {
        if (!date) return null;
        return add ?  new Date(date.getTime() + 60000) : new Date(date.getTime() - 60000); // 60000 milliseconds = 1 minute
    }

    export function helpOptionsFunc(isShareIndia: boolean, userManualLink: string, productVideos: string, faqsLink: string, supportInfo: HelpInfoInterface) {
        let helpOptions: HelpOptionsInterface[];
        if (isShareIndia) {
            helpOptions = [
                {
                    title: 'User Manual',
                    imageIcon: '/assets/infoHelp.svg',
                    description: HELP_SECTION_STRINGS.USER_MANUAL,
                    button: 'Download User Manual',
                    link: userManualLink,
                },
                {
                    title: 'Videos',
                    imageIcon: '/assets/videoHelp.svg',
                    description: HELP_SECTION_STRINGS.VIDEOS,
                    button: 'Go to Videos',
                    link: productVideos
                },
                {
                    title: 'FAQs',
                    imageIcon: '/assets/faqsHelp.svg',
                    description: HELP_SECTION_STRINGS.FAQS,
                    button: 'Go to FAQs',
                    link: faqsLink
                },
                {
                    title: 'Whatsapp',
                    imageIcon: '/assets/whatsappHelp.svg',
                    description: HELP_SECTION_STRINGS.WHATSAPP,
                    button: 'Message us on WhatsApp',
                    link: `https://wa.me/${supportInfo.whatsapp}`,
                    extraDetails: supportInfo?.whatsapp
                },
                {
                    title: 'Call Us',
                    imageIcon: '/assets/callHelp.svg',
                    description: HELP_SECTION_STRINGS.CALLUS,
                    button: 'Call Us',
                    link: `tel:${supportInfo.phone}`,
                    extraDetails: supportInfo?.phone
                },
                {
                    title: 'Email Us',
                    imageIcon: '/assets/emailHelp.svg',
                    description: HELP_SECTION_STRINGS.EMAILUS,
                    button: 'Email Us',
                    link: `mailto:${supportInfo.email}`,
                    extraDetails: supportInfo?.email
                },
            ]
        }
        else {
            helpOptions = [
                {
                    title: 'Whatsapp',
                    imageIcon: '/assets/whatsappHelp.svg',
                    description: HELP_SECTION_STRINGS.WHATSAPP,
                    button: 'Message us on WhatsApp',
                    link: `https://wa.me/${supportInfo.whatsapp}`,
                    extraDetails: supportInfo?.whatsapp
                },
                {
                    title: 'Call Us',
                    imageIcon: '/assets/callHelp.svg',
                    description: HELP_SECTION_STRINGS.CALLUS,
                    button: 'Call Us',
                    link: `tel:${supportInfo.phone}`,
                    extraDetails: supportInfo?.phone
                },
                {
                    title: 'Email Us',
                    imageIcon: '/assets/emailHelp.svg',
                    description: HELP_SECTION_STRINGS.EMAILUS,
                    button: 'Email Us',
                    link: `mailto:${supportInfo.email}`,
                    extraDetails: supportInfo?.email
                },
            ]
        }
        return helpOptions;
    }

    // We pass it the Array of the all the symbols in a strategy and it gives us two strings 
    // Unique Symbol String which gives us three symbols to display at card level 
    // And All Unique Symbols (which is used as a tooltip) that gives us a string of all unique symbols.
    export function getUniqueSymbols(symbols: string[], showSymbolCount: number = 3): [string, string] {
        let unqiueSymbolSet = new Set()
        symbols?.forEach(symbol => unqiueSymbolSet.add(symbol))
        let uniqueSymbolArr = Array.from(unqiueSymbolSet);
        let unqiueSymbolStr = uniqueSymbolArr.slice(0, showSymbolCount).join(', ');
        let allUniqueSymbols = uniqueSymbolArr.join(', ');
        if (uniqueSymbolArr.length > showSymbolCount) {
            unqiueSymbolStr = unqiueSymbolStr.concat('...');
        }
        unqiueSymbolStr = unqiueSymbolStr === '' ? '--' : unqiueSymbolStr
        return [unqiueSymbolStr, allUniqueSymbols];
    }
    export function getAllowedExchangeProductsMap(plan: CurrentPlan, isCarryForwardAllowed: boolean, isIntradayAllowed: boolean) {
        const exchangeProductMap: Map<number, { [key: number]: string }> = new Map();
        const exchangeStrings = getAllowedExchanges(plan);
        for (let permission of plan.plan.algo_permissions) {
            switch (permission) {
                case AlgoPermissions.WEBALGOPERMISSION_NSECM_CARRYFORWARD:
                    if (isCarryForwardAllowed && exchangeStrings[ExchangeId.EXCHANGEID_NSECM]) {
                        let products = exchangeProductMap.get(ExchangeId.EXCHANGEID_NSECM) || {};
                        products[ProductType.PRODUCTTYPE_CARRYFORWARD] = PRODUCT_OPTIONS[ProductType.PRODUCTTYPE_CARRYFORWARD];
                        exchangeProductMap.set(ExchangeId.EXCHANGEID_NSECM, products);
                    }
                    break;
                case AlgoPermissions.WEBALGOPERMISSION_NSECM_INTRADAY:
                    if (isIntradayAllowed && exchangeStrings[ExchangeId.EXCHANGEID_NSECM]) {
                        let products = exchangeProductMap.get(ExchangeId.EXCHANGEID_NSECM) || {};
                        products[ProductType.PRODUCTTYPE_INTRADAY] = PRODUCT_OPTIONS[ProductType.PRODUCTTYPE_INTRADAY];
                        exchangeProductMap.set(ExchangeId.EXCHANGEID_NSECM, products);
                    }
                    break;
                case AlgoPermissions.WEBALGOPERMISSION_BSECM_CARRYFORWARD:
                    if (isCarryForwardAllowed && exchangeStrings[ExchangeId.EXCHANGEID_BSECM]) {
                        let products = exchangeProductMap.get(ExchangeId.EXCHANGEID_BSECM) || {};
                        products[ProductType.PRODUCTTYPE_CARRYFORWARD] = PRODUCT_OPTIONS[ProductType.PRODUCTTYPE_CARRYFORWARD];
                        exchangeProductMap.set(ExchangeId.EXCHANGEID_BSECM, products);
                    }
                    break;
                case AlgoPermissions.WEBALGOPERMISSION_BSECM_INTRADAY:
                    if (isIntradayAllowed && exchangeStrings[ExchangeId.EXCHANGEID_BSECM]) {
                        let products = exchangeProductMap.get(ExchangeId.EXCHANGEID_BSECM) || {};
                        products[ProductType.PRODUCTTYPE_INTRADAY] = PRODUCT_OPTIONS[ProductType.PRODUCTTYPE_INTRADAY];
                        exchangeProductMap.set(ExchangeId.EXCHANGEID_BSECM, products);
                    }
                    break;
                case AlgoPermissions.WEBALGOPERMISSION_NSEFO_CARRYFORWARD:
                    if (isCarryForwardAllowed && exchangeStrings[ExchangeId.EXCHANGEID_NSEFO]) {
                        let products = exchangeProductMap.get(ExchangeId.EXCHANGEID_NSEFO) || {};
                        products[ProductType.PRODUCTTYPE_CARRYFORWARD] = PRODUCT_OPTIONS[ProductType.PRODUCTTYPE_CARRYFORWARD];
                        exchangeProductMap.set(ExchangeId.EXCHANGEID_NSEFO, products);
                    }
                    break;
                case AlgoPermissions.WEBALGOPERMISSION_NSEFO_INTRADAY:
                    if (isIntradayAllowed && exchangeStrings[ExchangeId.EXCHANGEID_NSEFO]) {
                        let products = exchangeProductMap.get(ExchangeId.EXCHANGEID_NSEFO) || {};
                        products[ProductType.PRODUCTTYPE_INTRADAY] = PRODUCT_OPTIONS[ProductType.PRODUCTTYPE_INTRADAY];
                        exchangeProductMap.set(ExchangeId.EXCHANGEID_NSEFO, products);
                    }
                    break;
                case AlgoPermissions.WEBALGOPERMISSION_BSEFO_CARRYFORWARD:
                    if (isCarryForwardAllowed && exchangeStrings[ExchangeId.EXCHANGEID_BSEFO]) {
                        let products = exchangeProductMap.get(ExchangeId.EXCHANGEID_BSEFO) || {};
                        products[ProductType.PRODUCTTYPE_CARRYFORWARD] = PRODUCT_OPTIONS[ProductType.PRODUCTTYPE_CARRYFORWARD];
                        exchangeProductMap.set(ExchangeId.EXCHANGEID_BSEFO, products);
                    }
                    break;
                case AlgoPermissions.WEBALGOPERMISSION_BSEFO_INTRADAY:
                    if (isIntradayAllowed && exchangeStrings[ExchangeId.EXCHANGEID_BSEFO]) {
                        let products = exchangeProductMap.get(ExchangeId.EXCHANGEID_BSEFO) || {};
                        products[ProductType.PRODUCTTYPE_INTRADAY] = PRODUCT_OPTIONS[ProductType.PRODUCTTYPE_INTRADAY];
                        exchangeProductMap.set(ExchangeId.EXCHANGEID_BSEFO, products);
                    }
                    break;
            }
        }
        return exchangeProductMap;
    }

    export function isAIGeneratedPortfolio(portfolio: PortfolioDisplayResponse | BriefPortfolioModel | PortfolioModel): boolean {
        return (portfolio.creationMethod === STRATEGY_CREATION_METHOD.YML_PORTFOLIO_CREATION) || (portfolio.creationMethod === STRATEGY_CREATION_METHOD.TV_YML_PORTFOLIO_CREATION);
    }
    
    export function getPortfolioMixPanelType(portfolio: PortfolioDisplayResponse | BriefPortfolioModel, isAIGeneratedPortfolio: boolean): string {
        return isAIGeneratedPortfolio ? "AI Generated" : portfolio.isPublic ? "Original" : "Custom";
    }

    export function frequencyMapping(planFrequency: string) {
        if (planFrequency.startsWith('Every')) {
            let frequencyArr = planFrequency.replace('Every-', '').split('-');
            frequencyArr[1] = frequencyMappingMultiple[frequencyArr[1]];
            return frequencyArr.join('_');
        }
        return planFrequency;
    }

    export function getPercentage(val: number) {
        return val / 100;
    }
    export function openInNewTab(url: string) {
        window.open(url, "_blank");
    }

    export function navigateToUrl(url: string): void {
        window.location.assign(url);
    }


    export async function readFile(file: File){
        return new Promise<string>((res,_)=>{
            const reader = new FileReader();
            reader.onload = function (e) {
                res(e.target.result as string);
            };
            reader.readAsText(file);
        })
    }

    export function preventSpaceAtBeginning(event: KeyboardEvent) {
        const input = event.target as HTMLInputElement;
        const cursorPosition = input.selectionStart;
        
        // If cursor is at position 0 and user tries to type a space, prevent it
        if (cursorPosition === 0 && event.key === ' ') {
            event.preventDefault();
            return;
        }
        
        // Allow all other characters including spaces after the first character
        return true;
    }


    export async function downloadAsXlsx(data: Array<Array<string>>, fileName?: string){
        let excelData = '';
        data.forEach((infoArray) => {
           excelData+=infoArray.join(",")
           excelData+='\r\n';
        });
        const downloader = new XlsxDownloadHelper();
        await downloader.formatXLSXData(data)
        downloader.download(fileName || new Date().valueOf().toString())
    }

    export function getNextDependentLegName(dependentLegs: DependentLeg[]): string {
        let nextCount = dependentLegs.length + 1;
        let name = `Dependent Leg ${nextCount}`;
        while (dependentLegs.some(leg => leg.name === name)) {
            nextCount++;
            name = `Dependent Leg ${nextCount}`;
        }
        return name;
    }

    export function getProductTypeString(productType: number) {
        return productType === ProductType.PRODUCTTYPE_CARRYFORWARD ? "Carryforward" : "Intraday";
    }

    export function _getFromDate(resolution: string, isTradingView?: boolean) {
        const yesterday = new Date();

        if (isTradingView) {
            if (resolution.endsWith('m')) {
                yesterday.setDate(yesterday.getDate() - 1);
            } else if (resolution === '1h') {
                yesterday.setDate(yesterday.getDate() - 5);
            } else if (resolution === '1d') {
                yesterday.setMonth(yesterday.getMonth() - 1);
            } else if (resolution === '1w') {
                yesterday.setFullYear(yesterday.getFullYear() - 1);
            } else if (resolution === '1M') {
                yesterday.setFullYear(yesterday.getFullYear() - 5);
            } else {
                yesterday.setFullYear(yesterday.getFullYear() - 1);
            }
        } else {
            if (
                resolution[resolution.length - 1] === 'h' ||
                resolution[resolution.length - 1] === 'm'
            ) {
                yesterday.setDate(yesterday.getDate() - 1);
            } else if (resolution[resolution.length - 1] === 'd') {
                yesterday.setDate(yesterday.getDate() - 7);
            } else if (resolution[resolution.length - 1] === 'w') {
                yesterday.setMonth(yesterday.getMonth() - 1);
            } else {
                yesterday.setFullYear(yesterday.getFullYear() - 1);
            }
        }

        return gmtToIst(yesterday.getTime());
    }

    export function _getToDate() {
        const today = new Date();

        return gmtToIst(today.getTime());
    }

    export function sanitizeUrl(url: string | undefined): string {
        if (!url || typeof url !== 'string') return '';
        try {
            const u = new URL(url);
            return `${u.protocol}//${u.host}${u.pathname}`;
        } catch {
            return url.split('?')[0];
        }
    }

    export function changeUrlToWss(url: string): string {
        if (!url) return '';
        return url.replace('https://', 'wss://');
    }

    /**
     * Clears all browser cookies except those in the COOKIE_NAMES allowlist.
     * 
     * Purpose: Prevents cookie bloat and removes stale/unnecessary third-party cookies.
     * Called on app bootstrap to ensure a clean cookie state.
     * 
     * Implementation details:
     * - Preserves allowlisted cookies defined in COOKIE_NAMES (e.g., session expiry)
     * - Handles both host-only cookies and domain-scoped cookies (shared across subdomains)
     * - Special handling for localhost and IP addresses which cannot use Domain attribute
     * - Uses both 'expires' and 'max-age' attributes for better browser compatibility
     * 
     * Edge cases:
     * - Skips execution if running in non-browser environment (SSR-safe)
     * - IP addresses: Cannot use domain attribute per RFC 6265
     * - localhost: Cannot use domain attribute
     * - Subdomains: Attempts cleanup with parent domain to clear subdomain cookies
     * 
     * @example
     * // In app initialization
     * Utils.clearCookies(); // Clears all cookies except allowlisted ones
     */
    export function clearCookies(): void {
        // SSR-safe check: only run in browser environment
        if (typeof window === 'undefined' || typeof document === 'undefined') {
            return;
        }

        const cookieNames = Object.values(COOKIE_NAMES);
        const hostname = window.location.hostname;
        const isIpAddress = /^\d{1,3}(\.\d{1,3}){3}$/.test(hostname);
        const canUseDomainAttribute = hostname !== 'localhost' && hostname.includes('.') && !isIpAddress;
        const parentDomain = canUseDomainAttribute ? hostname.split('.').slice(-2).join('.') : undefined;

        document.cookie.split(';').forEach(cookie => {
            const part = cookie.trim();
            if (!part) return;

            const eqPos = part.indexOf('=');
            const name = (eqPos > -1 ? part.substring(0, eqPos) : part).trim();
            if (!name || cookieNames.includes(name)) return;

            // Clear host-only cookie (no Domain attribute).
            document.cookie = `${name}=; expires=${EPOCH_EXPIRES}; max-age=0; path=/;`;

            // Best-effort clear for cookies set with Domain attribute (shared across subdomains).
            if (parentDomain) {
                document.cookie = `${name}=; expires=${EPOCH_EXPIRES}; max-age=0; domain=${parentDomain}; path=/;`;
            }
        });
    }

    export function getAmountBucket(amount: number | null): string {
        if (amount === null || amount === undefined) return 'unknown';
        const abs = Math.abs(amount);
        if (abs < 10000) return '< ₹10k';
        if (abs < 50000) return '₹10k-50k';
        if (abs < 200000) return '₹50k-2L';
        if (abs < 1000000) return '₹2L-10L';
        if (abs < 5000000) return '₹10L-50L';
        return 'In Crores';
    }
}



export type TypedControls<T> = { [Key in keyof T]: AbstractControl }
export type TypedFormGroup<T> = Omit<UntypedFormGroup, 'value' | 'controls'> & { value: T, controls: TypedControls<T> }
